import {getTestProps} from '@benchling/js-test-utils';
import {Margin} from '@visx/xychart';
import React from 'react';

import {useBenchlingChartContext} from '../BenchlingChartContext';
import getChartDimensions from '../utils/getChartDimensions';

export function getChartMaskId(clientId: string): string {
  return `${clientId}-chart-mask`;
}

interface ChartMaskProps {
  margins?: Margin;
  width: number;
  height: number;
}

/**
 * A mask that covers only a chart's plot area, not its margin area.
 *
 * To use the mask, render an svg element with:
 * ```
 *   <elem mask={`url(#${getChartMaskId(clientId)})`} ... />
 * ```
 */
export default function ChartMask({
  margins = {left: 0, right: 0, top: 0, bottom: 0},
  width,
  height,
}: ChartMaskProps): JSX.Element {
  const {clientId} = useBenchlingChartContext();
  const {chartWidth, chartHeight} = getChartDimensions(margins, width, height);
  return (
    // Since there can be multiple charts on the same page and svg uses url(#id) to refer to the mask,
    // each chart should have its own unique mask id so the url call knows which mask to use.
    <mask
      id={getChartMaskId(clientId)}
      fill="none"
      {...getTestProps({component: 'ChartMask', element: 'mask'})}
    >
      <rect
        x={margins.left}
        y={margins.top}
        width={chartWidth}
        height={chartHeight}
        fill="white"
        {...getTestProps({component: 'ChartMask', element: 'rect'})}
      />
    </mask>
  );
}
